import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { fetchOwners, GetOwnerDataPayload, updateCompany } from '../../../../store/investor/action';
import { getDealFormConfig } from '../../../../store/investor/apiHelpers';
import { fetchOrgInfoById } from '../../../../store/investor/investorApiHelper';
import { VIEW_CONFIGS } from '../../../../utils/constants/commonConstants';
import { createDropdownOptions, isEmpty } from '../../../../utils/utils';
import { DataTableColumn } from '../../../../common/_custom';

interface UseMandateCreationModalProps {
    isOpen: boolean;
    onCreateMandate: (updatedMandateData?: any) => void;
}

export const useMandateCreationModal = ({ isOpen, onCreateMandate }: UseMandateCreationModalProps) => {
    const { id: assesseeOrgId } = useParams<{ id: string }>();
    const user = useAppSelector((state) => state.user);
    const partnerData = useAppSelector((state) => state.investor.partnerData);
    const ownerDropdownOptions = useAppSelector((state) => state.investor.ownerDropdownOptions);
    const dispatch = useAppDispatch();

    // State to store the current values of each field - initialized as empty
    const [mandateData, setMandateData] = useState<any>({});

    const [leadsColumnsConfig, setLeadsColumnsConfig] = useState<{ columns: DataTableColumn[] }>({ columns: [] });
    const [loading, setLoading] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);

    // Fetch leads columns config when modal opens
    const fetchLeadsConfig = async () => {
        if (!isOpen) return;

        try {
            await getDealFormConfig(
                {
                    accountType: user.userData.accountType,
                    investorOrgId: user.userData.investorOrgId,
                    primaryEmail: user.userData.primaryEmail,
                    configName: VIEW_CONFIGS.AICA_DEAL_NAVBAR_VIEW,
                },
                {
                    onSuccess: (configData) => {
                        setLeadsColumnsConfig(configData?.leadsColumns ?? { columns: [] });
                    },
                },
            );
        } catch (error) {
            console.error('Error fetching leads columns config:', error);
        }
    };

    const fetchOwnerDetails = () => {
        let payload: GetOwnerDataPayload = {
            investorOrgId: user.userData.investorOrgId,
        };
        dispatch(fetchOwners(payload));
    };

    // Fetch organization details and populate mandateData with existing data
    const fetchOrgDetails = async () => {
        if (!assesseeOrgId) return;

        try {
            const orgInfo = await fetchOrgInfoById({ orgId: assesseeOrgId });

            // Populate mandateData with existing owners and customFields
            setMandateData((prevData: any) => {
                const newData = { ...prevData };

                // Populate owners if they exist
                if (orgInfo?.owners) {
                    newData.owners = { ...orgInfo.owners };
                }

                // Populate customFields if they exist
                if (orgInfo?.customFields) {
                    newData.customFields = { ...orgInfo.customFields };
                }

                return newData;
            });
        } catch (error) {
            console.error('Error fetching organization details:', error);
        }
    };

    // Fetch config, owners, and organization details when modal opens
    useEffect(() => {
        if (isOpen && user.userData.investorOrgId && user.userData.accountType) {
            setLoading(true);
            fetchLeadsConfig();
            fetchOwnerDetails();
            fetchOrgDetails();
        }
    }, [isOpen, user.userData.investorOrgId, user.userData.accountType]);

    // Stop loading when both data sources are available
    useEffect(() => {
        if (ownerDropdownOptions && ownerDropdownOptions.length > 0 && leadsColumnsConfig.columns.length > 0) {
            setLoading(false);
        }
    }, [ownerDropdownOptions, leadsColumnsConfig.columns.length]);

    // Filter columns that have mandateCreationConfig.enabled: true
    const mandateColumns = leadsColumnsConfig?.columns?.filter((column: any) => column?.mandateCreationConfig?.enabled === true) || [];

    // Validation function to check if all mandate columns are filled
    const validateForm = (data: any, columns: any[]) => {
        if (!columns || columns.length === 0) return false;

        return columns.every((column: any) => {
            const field = column.field;

            // Handle nested fields (e.g., 'customFields.partnerOrgId')
            if (field.includes('.')) {
                const [parentKey, childKey] = field.split('.');
                const parentValue = data[parentKey];
                if (!parentValue || typeof parentValue !== 'object') return false;
                const value = parentValue[childKey];
                return !isEmpty(value);
            }

            // Handle owner fields
            if (field.startsWith('owners.')) {
                const ownerType = field.replace('owners.', '');
                const owners = data.owners || {};
                const ownerValue = owners[ownerType];
                return !isEmpty(ownerValue);
            }

            // Handle direct fields
            const value = data[field];
            return !isEmpty(value);
        });
    };

    // Create dropdown options for partner fields using createDropdownOptions utility
    const partnerDropdownOptions = useMemo(() => {
        if (!partnerData || !Array.isArray(partnerData)) return {};
        const filteredOrgs = partnerData.filter((org) => org?.organizationId && org?.organizationDisplayName);
        const params = filteredOrgs.map((org) => ({
            organizationId: org?.organizationId || '',
            organizationDisplayName: org?.organizationDisplayName || '',
        }));
        return createDropdownOptions(params);
    }, [partnerData]);

    const getStatusDropdownList = (column: any, row: any) => {
        const field = column.field;
        switch (field) {
            case 'customFields.partnerOrgId':
                return partnerDropdownOptions;
            default:
                return column.dropdownOptions || {};
        }
    };

    /**
     * Helper function to update nested fields in mandateData state
     * Handles both flat fields and nested fields with dot notation (e.g., 'customFields.partnerOrgId')
     *
     * @param field - The field path (supports dot notation for nested fields)
     * @param value - The value to set
     * @param data - The current data object to update
     * @returns Updated data object with the new value set
     */
    const updateNestedField = (field: string, value: any, data: any) => {
        const newData = { ...data };

        if (field.includes('.')) {
            const [parentKey, childKey] = field.split('.');
            if (!newData[parentKey]) {
                newData[parentKey] = {};
            }
            newData[parentKey][childKey] = value;
        } else {
            newData[field] = value;
        }

        return newData;
    };

    /**
     * Updates custom field values in the mandate data
     * Used for text inputs, Numbers and other custom field updates
     *
     * @param row - The table row data (unused but kept for API compatibility)
     * @param updateData - Object containing the new value
     * @param column - Column configuration containing the field name
     * @param handlers - Optional callback handlers for start/success events
     */
    const updateCustomFieldStatus = (row: any, updateData: any, column: any, handlers?: any) => {
        const { value } = updateData;
        const field = column.field;

        handlers?.onStart?.();

        setMandateData((prevData: any) => updateNestedField(field, value, prevData));

        handlers?.onSuccess?.();
    };

    /**
     * Updates single select dropdown values in the mandate data
     * Used for dropdown selections where only one option can be selected
     *
     * @param params - Object containing row, column, and selected option
     */
    const updateSingleSelectDropdown = (params: any) => {
        const { row, column, option } = params;
        const field = column.field;

        setMandateData((prevData: any) => updateNestedField(field, option.value, prevData));
    };

    /**
     * Updates owner assignments in the mandate data
     * Handles owner-specific updates stored in the owners object
     *
     * @param params - Object containing row, value, ownerType, and headerId
     */
    const updateOwner = (params: any) => {
        const { row, value, ownerType, headerId } = params;

        setMandateData((prevData: any) => {
            const newData = { ...prevData };

            // Ensure owners object exists
            if (!newData.owners) {
                newData.owners = {};
            }

            newData.owners[headerId] = value;
            return newData;
        });
    };

    /**
     * Creates a bulk update payload for the updateCompany API call
     * Transforms the mandate data into the format expected by the updateOrgMaster api
     *
     * @param mandateData - The current mandate data object containing all field updates
     * @returns Formatted payload object ready for API submission
     */
    const createBulkUpdatePayload = (mandateData: any) => {
        const payload: any = {
            orgId: assesseeOrgId || '',
        };

        // Process owners - create owners array
        if (mandateData.owners && Object.keys(mandateData.owners).length > 0) {
            payload.owners = Object.entries(mandateData.owners).map(([ownerType, userId]) => {
                // Find owner name from dropdown options
                const owner = ownerDropdownOptions.find((opt) => opt.userId === userId);
                return {
                    name: owner?.name || '',
                    userId: userId as string,
                    activityBy: user.userData.userName,
                    activityDate: new Date().toISOString(),
                    ownerType: ownerType,
                };
            });
        }

        // Process custom fields - only add if they exist
        if (mandateData.customFields && Object.keys(mandateData.customFields).length > 0) {
            payload.customFields = { ...mandateData.customFields };
        }

        // Process other direct fields (excluding owners, customFields, and system fields)
        const excludedKeys = new Set(['owners', 'customFields', 'assesseeOrgId', 'rowStyle']);
        Object.keys(mandateData).forEach((key) => {
            if (!excludedKeys.has(key)) {
                payload[key] = mandateData[key];
            }
        });

        return payload;
    };

    const handleProceed = () => {
        // Create bulk update payload
        const bulkUpdatePayload = createBulkUpdatePayload(mandateData);

        // Call updateCompany with bulk payload
        dispatch(
            updateCompany(bulkUpdatePayload, {
                onSuccess: () => {
                    onCreateMandate(mandateData);
                },
                onError: () => {
                    console.error('Failed to update company');
                },
            }),
        );
    };

    // Validate form whenever mandateData or mandateColumns change
    useEffect(() => {
        const isValid = validateForm(mandateData, mandateColumns);
        setIsFormValid(isValid);
    }, [mandateData, mandateColumns]);

    // Reset mandate data when modal closes
    useEffect(() => {
        if (!isOpen) {
            setMandateData({});
            setIsFormValid(false);
        }
    }, [isOpen]);

    return {
        mandateData,
        mandateColumns,
        loading,
        isFormValid,
        updateCustomFieldStatus,
        updateSingleSelectDropdown,
        updateOwner,
        getStatusDropdownList,
        handleProceed,
    };
};
